import React, { useEffect, useState } from "react";
import { isTemplateKey, TEMPLATE_DRAFT_BADGE } from "../server/med-config/templates";
import { api } from "./api";
import { CONFIG_KEY_DEFS, configKeyDef, type ConfigKeyDef, type Field } from "../server/med-config/keys";

/**
 * Config (build plan §7.1 #1): one card per MedConfig key, each with its own
 * editor built from the same field definitions the server validates with.
 * Keys the user may not edit are shown read-only (plan §5). Every save is
 * audited on the server with before/after.
 */

interface Entry {
  key: string;
  label: string;
  value: unknown;
  blank: boolean;
  updatedByEmail: string;
  updatedUtc: string;
  editable: boolean;
  /** A notification template: whether this user may set Approved (an RN only). */
  mayApprove?: boolean;
}
interface Readiness {
  ready: boolean;
  blank: { key: string; label: string }[];
  duplicates: string[];
}

/** Editor state: strings everywhere, lists as one-per-line text, rows as arrays. */
type Draft = string | Draft[] | { [k: string]: Draft };

function fieldDraft(f: Field, v: unknown): Draft {
  if (f.kind === "group") return objDraft(f.fields ?? [], v);
  if (f.kind === "rows") return Array.isArray(v) ? v.map((row) => objDraft(f.fields ?? [], row)) : [];
  if (f.kind === "lines") return Array.isArray(v) ? v.join("\n") : "";
  return v === undefined || v === null ? "" : String(v);
}
function objDraft(fields: readonly Field[], v: unknown): { [k: string]: Draft } {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  return Object.fromEntries(fields.map((f) => [f.name, fieldDraft(f, o[f.name])]));
}
function toDraft(def: ConfigKeyDef, value: unknown): Draft {
  if (def.shape === "lines") return Array.isArray(value) ? value.join("\n") : "";
  if (def.shape === "rows") return Array.isArray(value) ? value.map((row) => objDraft(def.fields, row)) : [];
  return objDraft(def.fields, value);
}

const isBlank = (d: Draft): boolean =>
  typeof d === "string" ? d.trim() === "" : Array.isArray(d) ? d.every(isBlank) : Object.values(d).every(isBlank);

function fromField(f: Field, d: Draft): unknown {
  if (f.kind === "group") return fromObj(f.fields ?? [], d as { [k: string]: Draft });
  if (f.kind === "rows") return (d as Draft[]).filter((r) => !isBlank(r)).map((r) => fromObj(f.fields ?? [], r as { [k: string]: Draft }));
  if (f.kind === "lines") return (d as string).split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  return d as string;
}
function fromObj(fields: readonly Field[], d: { [k: string]: Draft }): Record<string, unknown> {
  return Object.fromEntries(fields.map((f) => [f.name, fromField(f, d[f.name])]));
}
function fromDraft(def: ConfigKeyDef, d: Draft): unknown {
  if (def.shape === "lines") return (d as string).split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
  if (def.shape === "rows") return (d as Draft[]).filter((r) => !isBlank(r)).map((r) => fromObj(def.fields, r as { [k: string]: Draft }));
  return fromObj(def.fields, d as { [k: string]: Draft });
}

function eastern(iso: string): string {
  if (!iso) return "";
  const t = new Date(iso);
  if (Number.isNaN(t.getTime())) return "";
  return new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }).format(t);
}

const INPUT_TYPE: Partial<Record<Field["kind"], string>> = { email: "email", phone: "tel", url: "url", date: "date", time: "time" };

function FieldInput({ f, d, onChange, disabled, id }: { f: Field; d: Draft; onChange: (d: Draft) => void; disabled: boolean; id: string }) {
  if (f.kind === "group") {
    return (
      <fieldset className="rounded-md border border-slate-200 p-3">
        <legend className="px-1 text-sm font-medium text-slate-700">{f.label}{f.required ? " *" : ""}</legend>
        {f.hint && <p className="field-hint mb-2">{f.hint}</p>}
        <Fields fields={f.fields ?? []} d={d as { [k: string]: Draft }} onChange={onChange} disabled={disabled} idPrefix={id} />
      </fieldset>
    );
  }
  if (f.kind === "rows") return <RowsInput fields={f.fields ?? []} rows={d as Draft[]} onChange={onChange} disabled={disabled} idPrefix={id} />;
  const label = (
    <label htmlFor={id} className="field-label">
      {f.label}
      {f.required ? " *" : ""}
    </label>
  );
  let control: React.ReactNode;
  if (f.kind === "lines" || f.kind === "paragraph") {
    control = <textarea id={id} className={`field-input ${f.kind === "paragraph" ? "min-h-32" : "min-h-24"}`} value={d as string} disabled={disabled} onChange={(e) => onChange(e.target.value)} />;
  } else if (f.kind === "yn" || f.kind === "choice") {
    const opts = f.kind === "yn" ? [["Y", "Yes"], ["N", "No"]] : (f.options ?? []).map((o) => [o, o.replace(/([a-z])([A-Z])/g, "$1 $2")]);
    control = (
      <select id={id} className="field-input max-w-xs" value={d as string} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {opts.map(([v, l]) => (
          <option key={v} value={v}>{l}</option>
        ))}
      </select>
    );
  } else {
    control = (
      <input
        id={id}
        className={`field-input ${f.kind === "number" || f.kind === "date" || f.kind === "time" ? "max-w-[12rem]" : ""}`}
        type={INPUT_TYPE[f.kind] ?? "text"}
        inputMode={f.kind === "number" ? "numeric" : undefined}
        value={d as string}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }
  return (
    <div>
      {label}
      {control}
      {f.hint && <p className="field-hint">{f.hint}</p>}
    </div>
  );
}

function Fields({ fields, d, onChange, disabled, idPrefix, lockedField }: { fields: readonly Field[]; d: { [k: string]: Draft }; onChange: (d: Draft) => void; disabled: boolean; idPrefix: string; lockedField?: string | readonly string[] }) {
  const locked = (name: string) => (Array.isArray(lockedField) ? lockedField.includes(name) : name === lockedField);
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {fields.map((f) => (
        <div key={f.name} className={f.kind === "group" || f.kind === "rows" || f.kind === "lines" || f.kind === "paragraph" ? "sm:col-span-2" : ""}>
          <FieldInput f={f} d={d[f.name]} disabled={disabled || locked(f.name)} id={`${idPrefix}-${f.name}`} onChange={(v) => onChange({ ...d, [f.name]: v })} />
        </div>
      ))}
    </div>
  );
}

/**
 * A list of rows. With `keep` (Windows): a row already saved can't be removed
 * and its identifying field can't change; it is retired instead.
 */
function RowsInput({ fields, rows, onChange, disabled, idPrefix, keep }: { fields: readonly Field[]; rows: Draft[]; onChange: (d: Draft) => void; disabled: boolean; idPrefix: string; keep?: { by: string; saved: Set<string> } }) {
  const blankRow = () => Object.fromEntries(fields.map((f) => [f.name, fieldDraft(f, undefined)]));
  const isSaved = (row: Draft) => Boolean(keep && keep.saved.has(String((row as { [k: string]: Draft })[keep.by] ?? "").trim().toUpperCase()));
  return (
    <div className="space-y-3">
      {rows.map((row, i) => (
        <div key={i} className="rounded-md border border-slate-200 p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700">Row {i + 1}</span>
            {!disabled && (
              <div className="flex gap-2">
                <button type="button" className="text-sm text-slate-600 underline disabled:opacity-40" disabled={i === 0} onClick={() => onChange(rows.map((r, j) => (j === i - 1 ? rows[i] : j === i ? rows[i - 1] : r)))}>
                  Move up
                </button>
                {isSaved(row) ? (
                  <span className="text-xs text-slate-500">Saved: retire it instead of removing</span>
                ) : (
                  <button type="button" className="text-sm text-red-700 underline" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                    Remove
                  </button>
                )}
              </div>
            )}
          </div>
          <Fields
            fields={isSaved(row) ? fields.map((f) => (f.name === keep!.by ? { ...f, hint: "Fixed once saved." } : f)) : fields}
            d={row as { [k: string]: Draft }}
            disabled={disabled}
            lockedField={isSaved(row) ? keep!.by : undefined}
            idPrefix={`${idPrefix}-${i}`}
            onChange={(v) => onChange(rows.map((r, j) => (j === i ? v : r)))}
          />
        </div>
      ))}
      {!disabled && (
        <button type="button" className="btn-secondary" onClick={() => onChange([...rows, blankRow()])}>
          Add a row
        </button>
      )}
    </div>
  );
}

function KeyCard({ entry, onSaved }: { entry: Entry; onSaved: (e: Entry) => void }) {
  const def = configKeyDef(entry.key)!;
  const [draft, setDraft] = useState<Draft>(() => toDraft(def, entry.value));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(draft) !== JSON.stringify(toDraft(def, entry.value));
  // A reload after any save (or someone else's) shows the stored value.
  useEffect(() => {
    setDraft(toDraft(def, entry.value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry.updatedUtc]);

  async function save(value: unknown) {
    setBusy(true);
    setError(null);
    setSaved(null);
    const r = await api<{ entry: Entry; changed: boolean }>("PUT", `/api/config/${encodeURIComponent(entry.key)}`, { value, expectedUpdatedUtc: entry.updatedUtc });
    setBusy(false);
    if (!r.ok) {
      setError(r.data.error ?? "Save failed.");
      return;
    }
    setDraft(toDraft(def, r.data.entry.value));
    setSaved(r.data.changed ? "Saved." : "No change.");
    onSaved(r.data.entry);
  }

  const id = `cfg-${entry.key}`;
  return (
    <form
      className="section-card"
      aria-labelledby={`${id}-h`}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void save(fromDraft(def, draft));
      }}
    >
      <div className="mb-1 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 id={`${id}-h`} className="text-base font-semibold">{def.label}</h3>
        {/* A parent-notification template (M4 Stage 2): its review status, until an RN approves the wording. */}
        {isTemplateKey(def.key) && (
          (entry.value as { status?: string } | null)?.status === "Approved" ? (
            <span className="rounded bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900">Approved by an RN{(entry.value as { approvedOn?: string }).approvedOn ? ` (${(entry.value as { approvedOn?: string }).approvedOn})` : ""}</span>
          ) : (
            <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-950">{TEMPLATE_DRAFT_BADGE}</span>
          )
        )}
        {entry.blank ? (
          <span className={`rounded px-2 py-0.5 text-xs font-medium ${def.requiredForGoLive ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-600"}`}>
            {def.requiredForGoLive ? "Blank — needed before go-live" : "Blank"}
          </span>
        ) : (
          <span className="text-xs text-slate-500">
            Last changed {eastern(entry.updatedUtc)}{entry.updatedByEmail ? ` by ${entry.updatedByEmail}` : ""}
          </span>
        )}
      </div>
      <p className="field-hint mb-3">{def.help}{entry.editable ? "" : " Only a System Admin can change this."}</p>
      {def.shape === "lines" ? (
        <FieldInput f={{ name: "v", label: "One per line", kind: "lines" }} d={draft} onChange={setDraft} disabled={!entry.editable || busy} id={id} />
      ) : def.shape === "rows" ? (
        <RowsInput
          fields={def.fields}
          rows={draft as Draft[]}
          onChange={setDraft}
          disabled={!entry.editable || busy}
          idPrefix={id}
          keep={def.keepRowsBy ? { by: def.keepRowsBy, saved: new Set((Array.isArray(entry.value) ? entry.value : []).map((r) => String((r as Record<string, unknown>)[def.keepRowsBy!] ?? "").trim().toUpperCase())) } : undefined}
        />
      ) : (
        <>
          {/* A template: the approval stamp is the server's; only an RN sets Approved (owner, 2026-10-20). */}
          {isTemplateKey(def.key) && entry.editable && !entry.mayApprove && <p className="field-hint mb-2">You can edit the subject and wording; it saves as Draft. Only an RN sets Approved.</p>}
          <Fields fields={def.fields} d={draft as { [k: string]: Draft }} onChange={setDraft} disabled={!entry.editable || busy} idPrefix={id} lockedField={isTemplateKey(def.key) ? ["approvedBy", "approvedOn", ...(entry.mayApprove ? [] : ["status"])] : undefined} />
        </>
      )}
      {error && <p className="field-error mt-2" role="alert">{error}</p>}
      {saved && <p className="mt-2 text-sm text-green-700" role="status">{saved}</p>}
      {entry.editable && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button type="submit" className="btn-primary" disabled={busy || !dirty}>{busy ? "Saving…" : "Save"}</button>
          {dirty && (
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => { setDraft(toDraft(def, entry.value)); setError(null); }}>
              Undo changes
            </button>
          )}
          {!entry.blank && !dirty && !def.keepRowsBy && (
            <button type="button" className="btn-secondary" disabled={busy} onClick={() => void save(null)}>
              Clear
            </button>
          )}
        </div>
      )}
    </form>
  );
}

export function Config({ onReadiness }: { onReadiness?: () => void }): React.ReactElement {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  const [readiness, setReadiness] = useState<Readiness | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    const r = await api<{ entries: Entry[]; readiness: Readiness }>("GET", "/api/config");
    if (!r.ok) {
      setError(r.data.error ?? "Could not load the settings.");
      return;
    }
    setEntries(r.data.entries);
    setReadiness(r.data.readiness);
  }
  useEffect(() => {
    void load();
  }, []);

  if (error) return <div className="section-card" role="alert"><p className="text-sm text-red-700">{error}</p></div>;
  if (!entries || !readiness) return <p className="text-sm text-slate-600">Loading…</p>;

  const order = CONFIG_KEY_DEFS.map((d) => d.key);
  const sorted = [...entries].sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
  return (
    <section>
      <h2 className="mb-1 text-xl font-semibold">Config</h2>
      <p className="mb-4 text-sm text-slate-600">
        {readiness.ready
          ? "Every setting needed for go-live is filled in."
          : `${readiness.blank.length} setting${readiness.blank.length === 1 ? "" : "s"} still blank before go-live. Applicant review works meanwhile.`}
      </p>
      {readiness.duplicates.length > 0 && (
        <p className="section-card text-sm text-amber-900" role="note">
          More than one row exists for: {readiness.duplicates.join(", ")}. The first is used; ask the owner to remove the extra row in SharePoint.
        </p>
      )}
      {sorted.map((e) => (
        <KeyCard
          key={e.key}
          entry={e}
          onSaved={() => {
            void load();
            onReadiness?.();
          }}
        />
      ))}
    </section>
  );
}
